import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { importFreshBudget, pushModal } from 'loot-core/client/actions';

import { useAuth } from '../../../hooks/useAuth';
import { useNavigate } from '../../../hooks/useNavigate';
import { AnimatedLoading } from '../../../icons/AnimatedLoading';
import DefaultBudgetIcon from '../../../icons/v2/DefaultBudget.svg';
import GuidedOnboardingIcon from '../../../icons/v2/GuidedOnboarding.svg';
import { useResponsive } from '../../../ResponsiveProvider';
import { theme } from '../../../style';
import { Button, ButtonWithLoading } from '../../common/Button';
import { Card } from '../../common/Card';
import { Text } from '../../common/Text';
import { View } from '../../common/View';

import { OnboardingMintModal } from './OnboardingMintModal';
import { useSessionFlag } from '../../../hooks/useSessionFlag';

export function OnboardingFree() {
  const navigate = useNavigate();
  const [showMintModal, setShowMintModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const { isNarrowWidth } = useResponsive();
  const { refreshAuthState, billingStatus } = useAuth();
  const [postAuth, setPostAuth] = useSessionFlag('postAuth');

  const initAuthState = async () => {
    const state = await refreshAuthState();
    if (!state.isAuthenticated) {
      navigate('/signup');
      return;
    }
  };

  useEffect(() => {
    initAuthState();

    // handle ynab opening
    if (postAuth === 'ynab') {
      dispatch(pushModal('import-ynab5'));
      setPostAuth(null);
    }
  }, []);

  const skipGuidedOnboardingHandler = () => {
    setSubmitting(true);
    dispatch(importFreshBudget({ isEntirelySkipped: true }));
  };

  return (
    <Card
      style={{
        padding: 20,
        textAlign: 'center',
        maxWidth: 856,
        opacity: submitting ? 0.9 : 1,
      }}
    >
      {submitting && (
        <View
          style={{
            position: 'absolute',
            top: -20,
            left: -20,
            bottom: -20,
            right: -20,
            zIndex: 11000,
            justifyContent: 'center',
          }}
        >
          <AnimatedLoading width={40} color={theme.pageTextDark} />
        </View>
      )}
      <h2>How would you like to set up your budget?</h2>
      <View style={{ gap: 16 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: isNarrowWidth ? 'column' : 'row',
            flexWrap: 'wrap',
            gap: 20,
            padding: '0 16px',
            flex: 1,
          }}
        >
          <ButtonWithLoading
            onClick={() => navigate('/onboarding/start-fresh')}
            style={{
              flex: 1,
              boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
              border: 'none',
              padding: 12,
              cursor: 'pointer',
              minWidth: 185,
            }}
            childrenWrapperStyles={{
              flex: 1,
              gap: 8,
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: '100%',
            }}
            disabled={submitting}
          >
            <img
              src={GuidedOnboardingIcon}
              alt=""
              width="100%"
              height="auto"
              style={{
                transform: `scale(${isNarrowWidth ? 0.9 : 0.75})`,
                maxWidth: 224,
                ...(isNarrowWidth ? { aspectRatio: 16 / 9 } : {}),
              }}
            />
            <Text style={{ fontSize: 18 }}>Start guided onboarding</Text>
          </ButtonWithLoading>
          <ButtonWithLoading
            onClick={skipGuidedOnboardingHandler}
            style={{
              flex: 1,
              boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
              border: 'none',
              padding: 12,
              cursor: 'pointer',
              minWidth: 185,
            }}
            childrenWrapperStyles={{
              flex: 1,
              gap: 8,
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: '100%',
            }}
            disabled={submitting}
          >
            <img
              src={DefaultBudgetIcon}
              alt=""
              width="100%"
              height="auto"
              style={{
                transform: `scale(${isNarrowWidth ? 1.1 : 0.9})`,
                maxWidth: 224,
                ...(isNarrowWidth ? { aspectRatio: 16 / 9 } : {}),
              }}
            />
            <Text style={{ fontSize: 18 }}>Create empty budget</Text>
          </ButtonWithLoading>
        </div>
        <View>
          <Button
            type="bare"
            style={{
              color: theme.pageTextLight,
              cursor: 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
            onClick={e => {
              e.preventDefault();
              dispatch(pushModal('import-ynab5'));
            }}
            disabled={submitting}
          >
            <span style={{ verticalAlign: 'middle', marginRight: 6 }}>
              Import from
            </span>
            <span style={{ verticalAlign: 'middle' }}>
              <img
                width={50}
                src="/images/ynab-logo.png"
                alt="ynab-logo"
                style={{ display: 'inline-flex' }}
              />
            </span>
          </Button>
          <Button
            type="bare"
            style={{
              color: theme.pageTextLight,
              gap: 6,
              display: 'block',
              cursor: 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
            onClick={() => setShowMintModal(true)}
            disabled={submitting}
          >
            <span style={{ verticalAlign: 'middle', marginRight: 6 }}>
              Import from
            </span>
            <span style={{ verticalAlign: 'middle' }}>
              <img
                width={50}
                src="/images/mint-logo.png"
                alt="mint-logo"
                style={{ display: 'inline-flex' }}
              />
            </span>
          </Button>
          <Button
            type="bare"
            style={{
              color: theme.pageTextLight,
              cursor: 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
            onClick={e => {
              e.preventDefault();
              dispatch(pushModal('import-actual'));
            }}
            disabled={submitting}
          >
            Import from Good Steward
          </Button>
        </View>
      </View>
      {showMintModal && (
        <OnboardingMintModal onClose={() => setShowMintModal(false)} />
      )}
    </Card>
  );
}
